import { useState } from "react";

export interface UsePageEntryStateOptions<TError> {
  identity: string | number;
  isFetching: boolean;
  isReady: boolean;
  error: TError | null | undefined;
}

export interface PageEntryState<TError> {
  isPending: boolean;
  error: TError | undefined;
}

const pendingState: PageEntryState<never> = {
  isPending: true,
  error: undefined,
};

const readyState: PageEntryState<never> = {
  isPending: false,
  error: undefined,
};

/**
 * Remembers successful entry so later query transitions cannot replace an active page.
 * This history cannot be derived from the current query flags or reset with a key.
 */
export function usePageEntryState<TError>({
  identity,
  isFetching,
  isReady,
  error,
}: UsePageEntryStateOptions<TError>): PageEntryState<TError> {
  const [enteredIdentity, setEnteredIdentity] = useState<string | number>();
  const hasEntered = enteredIdentity === identity;

  if (hasEntered) {
    return readyState;
  }

  if (isFetching) {
    return pendingState;
  }

  if (error !== null && error !== undefined) {
    return {
      isPending: false,
      error,
    };
  }

  if (!isReady) {
    return pendingState;
  }

  // The identity mismatch becomes false on React's immediate retry of this render.
  setEnteredIdentity(identity);

  return readyState;
}
